import * as React from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router';
import { QueryClientProvider, useMutation } from '@tanstack/react-query';
import { queryClient } from 'common/react-query/query-client';
import { getProjectId, getReportUrl, getWorkspaceId } from 'common/report/util';
import { ReactErrorBoundary } from 'common/components/react-error-boundary';
import { ExperimentPage } from 'apps/experiments/src/components/page';
import { ExperimentListPage } from 'apps/experiments/src/components/list-page';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ToastType } from 'common/components/toast/types';
import { useTranslation } from 'common/i18n/hooks';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { NEW_EXPERIMENT_PARAMS } from 'apps/experiments/src/components/utils';
import styles from './index.module.styl';
import { useExperimentsApi } from './api/experiments';
type ViewProps = {
  projectId: number;
  workspaceId: number;
};
const View: React.FC<ViewProps> = props => {
  var _userCookieUtils$getU, _userCookieUtils$getU2;
  const {
    experimentId
  } = useParams<{
    experimentId: string;
  }>();
  return <ExperimentPage projectId={props.projectId} workspaceId={props.workspaceId} userId={(_userCookieUtils$getU = (_userCookieUtils$getU2 = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU2 === void 0 ? void 0 : _userCookieUtils$getU2.id) !== null && _userCookieUtils$getU !== void 0 ? _userCookieUtils$getU : 0} experimentId={experimentId!} data-sentry-element="ExperimentPage" data-sentry-component="View" data-sentry-source-file="index.tsx" />;
};
type ListProps = {
  projectId: number;
  workspaceId: number;
};
const List: React.FC<ListProps> = props => {
  var _userCookieUtils$getU3, _userCookieUtils$getU4;
  return <ExperimentListPage projectId={props.projectId} workspaceId={props.workspaceId} userId={(_userCookieUtils$getU3 = (_userCookieUtils$getU4 = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU4 === void 0 ? void 0 : _userCookieUtils$getU4.id) !== null && _userCookieUtils$getU3 !== void 0 ? _userCookieUtils$getU3 : 0} data-sentry-element="ExperimentListPage" data-sentry-component="List" data-sentry-source-file="index.tsx" />;
};
type CreateProps = {
  projectId: number;
  workspaceId: number;
};
const Create: React.FC<CreateProps> = _ref => {
  let {
    projectId,
    workspaceId
  } = _ref;
  const navigate = useNavigate();
  const i18n = useTranslation();
  const {
    createExperiment
  } = useExperimentsApi({
    projectId,
    workspaceId
  });
  const created = React.useRef(false);
  const createMutation = useMutation({
    mutationFn: createExperiment,
    onSuccess: response => {
      navigate(getReportUrl("experiments/".concat(response.id.toString()), {
        projectId,
        workspaceId
      }));
    },
    onError: () => {
      addToast({
        message: i18n.t("experiments.actions.create.fail"),
        type: ToastType.ERROR,
        timeout: 6000
      });
    }
  });
  React.useEffect(() => {
    if (!created.current && !createMutation.isPending) {
      created.current = true;
      createMutation.mutate(NEW_EXPERIMENT_PARAMS);
    }
  }, [createMutation]);
  return <SpinnerReact size={SpinnerSize.Large} data-sentry-element="SpinnerReact" data-sentry-component="Create" data-sentry-source-file="index.tsx" />;
};

// for lazy loading route
// eslint-disable-next-line import/no-default-export
export default function () {
  const projectId = parseInt(getProjectId(), 10);
  const workspaceId = parseInt(getWorkspaceId(), 10);
  return <div className={styles.appContainer}>
      <ReactErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route path='' element={<List projectId={projectId} workspaceId={workspaceId} />} />
            <Route path='new' element={<Create projectId={projectId} workspaceId={workspaceId} />} />
            <Route path=':experimentId' element={<View projectId={projectId} workspaceId={workspaceId} />} />
          </Routes>
        </QueryClientProvider>
      </ReactErrorBoundary>
    </div>;
}